import { useState, useEffect, useCallback } from 'react';
import { oktaAuth, isDemoMode } from './OktaAuth';
import { authToken } from './authToken';

export type AppRole = 'admin' | 'editor' | 'viewer';

export interface AuthUser {
  name: string;
  email: string;
  username: string;
  appRole: AppRole;
}

const DEMO_USER: AuthUser = {
  name: 'Demo User',
  email: 'demo@8x8.com',
  username: 'demo@8x8.com',
  appRole: 'admin', // demo mode = full access for local dev
};

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(isDemoMode ? DEMO_USER : null);
  const [loading, setLoading] = useState(!isDemoMode);

  useEffect(() => {
    if (isDemoMode || !oktaAuth) return;

    (async () => {
      try {
        // Handle redirect callback — then navigate away from /callback
        if (oktaAuth.isLoginRedirect()) {
          await oktaAuth.handleLoginRedirect();
          window.location.replace('/');
          return;
        }

        const isAuthenticated = await oktaAuth.isAuthenticated();
        if (isAuthenticated) {
          const info = await oktaAuth.getUser();
          const email = info.email || '';
          const name = info.name || info.preferred_username || 'User';
          const username = (info.preferred_username as string) || email;

          // Cache the ID token for installAuthFetch + manual API helpers.
          // The server middleware verifies it on every /api/* request.
          const idToken = oktaAuth.getIdToken();
          if (idToken) authToken.set(idToken);

          // Register login with backend — returns app_role.
          // Identity is established server-side from the verified Okta
          // ID token (Authorization: Bearer ...), not from body fields.
          let appRole: AppRole = 'viewer';
          try {
            const res = await fetch('/api/auth/me', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
              },
              body: JSON.stringify({ email, name, username }),
            });
            if (res.ok) {
              const me = (await res.json()) as { app_role?: unknown };
              if (me.app_role === 'admin' || me.app_role === 'editor' || me.app_role === 'viewer') {
                appRole = me.app_role;
              }
            }
          } catch {
            console.warn('Could not reach /api/auth/me, defaulting to viewer role');
          }

          setUser({ name, email, username, appRole });
        } else {
          await oktaAuth.signInWithRedirect();
        }
      } catch (err) {
        console.error('Auth error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signOut = useCallback(async () => {
    if (isDemoMode || !oktaAuth) return;
    authToken.clear();
    await oktaAuth.signOut();
  }, []);

  const isAdmin = user?.appRole === 'admin';
  const isEditor = user?.appRole === 'editor';
  const canEdit = isAdmin || isEditor;

  return { user, loading, signOut, isDemoMode, isAdmin, isEditor, canEdit };
}
